import { Badge, Button } from '@/components/UI';
import type { OptimizerConfirmationListItem } from './types';
import { formatSignedPct } from './helpers';

interface OptimizerConfirmationsPanelProps {
    confirmations: OptimizerConfirmationListItem[];
    errorMessage?: string | undefined;
    isError: boolean;
    isFetching: boolean;
    onRefresh: () => void;
}

function confirmationStatusVariant(status: OptimizerConfirmationListItem['confirmation']['status']) {
    if (status === 'completed') return 'success';
    if (status === 'failed' || status === 'cancelled') return 'danger';
    if (status === 'queued' || status === 'running') return 'info';
    return 'secondary';
}

function confirmationVerdictVariant(verdict: OptimizerConfirmationListItem['confirmation']['verdict']) {
    if (verdict === 'passed') return 'success';
    if (verdict === 'failed' || verdict === 'runtime_error') return 'danger';
    if (verdict === 'insufficient_data') return 'warning';
    return 'secondary';
}

function formatResultIds(resultIds: number[]) {
    if (resultIds.length === 0) return '-';
    if (resultIds.length <= 4) return resultIds.join(', ');
    return `${resultIds.slice(0, 4).join(', ')} +${resultIds.length - 4}`;
}

function formatReturnPercentage(value: number | string | null) {
    if (value === null) return '-';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return formatSignedPct(numeric, 2);
}

// Honest full-bundle breadth: the in-sample entries the bundle selects across the dataset.
function formatEntryCount(value: number | null) {
    if (value === null || !Number.isFinite(value)) return '–';
    return value.toLocaleString();
}

// Sample-scoped trade count rendered against the replayed sample size so it reads as sample-only.
function formatSampleTrades(totalTradeCount: number | null, sampleSize: number | null) {
    if (totalTradeCount === null || !Number.isFinite(totalTradeCount)) return '-';
    if (sampleSize === null || !Number.isFinite(sampleSize)) return totalTradeCount.toLocaleString();
    return `${totalTradeCount.toLocaleString()} / ${sampleSize.toLocaleString()}`;
}

// Extrapolate the sample execution rate + sample avg % to the full selected-entry coverage.
// Guard against missing inputs / a zero replay denominator so we never render NaN or Infinity.
function formatEstimatedReturnSum(confirmation: OptimizerConfirmationListItem['confirmation']) {
    const { selectedEntryCount } = confirmation;
    const { totalTradeCount } = confirmation;
    const { replayedEntryCount } = confirmation;
    const avgTradeProfit = confirmation.avgTradeProfit === null ? Number.NaN : Number(confirmation.avgTradeProfit);

    if (
        selectedEntryCount === null ||
        totalTradeCount === null ||
        replayedEntryCount === null ||
        replayedEntryCount === 0 ||
        !Number.isFinite(selectedEntryCount) ||
        !Number.isFinite(totalTradeCount) ||
        !Number.isFinite(replayedEntryCount) ||
        !Number.isFinite(avgTradeProfit)
    ) {
        return '–';
    }

    const estimate = selectedEntryCount * (totalTradeCount / replayedEntryCount) * avgTradeProfit;
    if (!Number.isFinite(estimate)) return '–';

    return formatSignedPct(estimate, 0);
}

function formatDateTime(value: string | Date | null) {
    if (!value) return '-';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
}

const OptimizerConfirmationsPanel = ({ confirmations, errorMessage, isError, isFetching, onRefresh }: OptimizerConfirmationsPanelProps) => {
    return (
        <section className="mb-4 border-y border-zinc-800 bg-zinc-950/40 py-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                    <div className="text-sm font-semibold text-zinc-100">Optimizer confirmations</div>
                    <div className="text-xs text-zinc-500">
                        {confirmations.length.toLocaleString()} recent run{confirmations.length === 1 ? '' : 's'}
                    </div>
                </div>
                <Button disabled={isFetching} size="sm" variant="secondary" onClick={onRefresh}>
                    {isFetching ? 'Refreshing...' : 'Refresh'}
                </Button>
            </div>

            {isError && (
                <div className="mb-3 text-sm text-red-300">Failed to load optimizer confirmations: {errorMessage ?? 'Unknown error'}</div>
            )}

            {!isError && confirmations.length === 0 ? (
                <div className="text-sm text-zinc-500">No optimizer confirmations have been queued for this job.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-xs">
                        <thead className="text-zinc-500">
                            <tr className="border-b border-zinc-800">
                                <th className="py-2 pr-3 font-medium">ID</th>
                                <th className="py-2 pr-3 font-medium">Rule</th>
                                <th className="py-2 pr-3 font-medium">Candidate</th>
                                <th className="py-2 pr-3 font-medium">Strategy</th>
                                <th className="py-2 pr-3 font-medium">Status</th>
                                <th className="py-2 pr-3 font-medium">Verdict</th>
                                <th className="py-2 pr-3 font-medium">Jobs</th>
                                <th
                                    className="py-2 pr-3 font-medium"
                                    title="Full in-sample entries this bundle selects across the dataset — the honest breadth signal, independent of the replayed sample."
                                >
                                    Coverage
                                </th>
                                <th
                                    className="py-2 pr-3 font-medium"
                                    title="Arithmetic return-sum estimate = coverage entries × sample execution rate (sample trades ÷ replayed entries) × sample average return. It is not compounded or a portfolio return."
                                >
                                    Est. return sum
                                </th>
                                <th
                                    className="py-2 pr-3 font-medium"
                                    title="Trades executed in the replayed sample only (~250 entries), not the full bundle."
                                >
                                    Trades (sample)
                                </th>
                                <th
                                    className="py-2 pr-3 font-medium"
                                    title="Average trade return measured on the replayed sample only (~250 entries), not the full bundle."
                                >
                                    Avg return (sample)
                                </th>
                                <th className="py-2 font-medium">Updated</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {confirmations.map(({ confirmation, resultIds }) => {
                                const canAttach = confirmation.status === 'completed' && confirmation.verdict === 'passed';

                                return (
                                    <tr className="text-zinc-300" key={confirmation.id}>
                                        <td className="py-2 pr-3 font-mono text-zinc-200">{confirmation.id}</td>
                                        <td className="py-2 pr-3 font-mono">
                                            {confirmation.screenerRuleId ?? '-'}
                                            {canAttach && confirmation.screenerRuleId !== null && (
                                                <Badge className="ml-2" text="attach ok" variant="success" />
                                            )}
                                        </td>
                                        <td className="py-2 pr-3 font-mono">
                                            {confirmation.candidateEvaluationId !== null
                                                ? `evaluation ${confirmation.candidateEvaluationId}`
                                                : formatResultIds(resultIds)}
                                        </td>
                                        <td className="py-2 pr-3 font-mono">{confirmation.strategyId}</td>
                                        <td className="py-2 pr-3">
                                            <Badge text={confirmation.status} variant={confirmationStatusVariant(confirmation.status)} />
                                        </td>
                                        <td className="py-2 pr-3">
                                            <Badge text={confirmation.verdict} variant={confirmationVerdictVariant(confirmation.verdict)} />
                                        </td>
                                        <td className="py-2 pr-3">
                                            {confirmation.completedJobCount.toLocaleString()}/{confirmation.queuedJobCount.toLocaleString()}
                                            {confirmation.failedJobCount > 0 && (
                                                <span className="ml-1 text-red-300">
                                                    ({confirmation.failedJobCount.toLocaleString()} failed)
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3 font-mono text-zinc-200">
                                            {formatEntryCount(confirmation.selectedEntryCount)}
                                        </td>
                                        <td className="py-2 pr-3 font-mono text-zinc-200">{formatEstimatedReturnSum(confirmation)}</td>
                                        <td className="py-2 pr-3">
                                            {formatSampleTrades(confirmation.totalTradeCount, confirmation.sampleSize)}
                                        </td>
                                        <td className="py-2 pr-3">{formatReturnPercentage(confirmation.avgTradeProfit)}</td>
                                        <td className="py-2">{formatDateTime(confirmation.updatedAt)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default OptimizerConfirmationsPanel;
